import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect } from "react";
import { createPortal } from "react-dom";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

export default function ClubImageLightbox({
  isOpen,
  images = [],
  currentIndex = 0,
  onClose,
  onIndexChange,
}) {
  const safeImages = Array.isArray(images) ? images.filter(Boolean) : [];
  const total = safeImages.length;
  const canStep = total > 1;
  const clampedIndex = Math.max(0, Math.min(currentIndex, Math.max(0, total - 1)));
  const currentSrc = safeImages[clampedIndex] || null;

  useEffect(() => {
    if (!isOpen || !currentSrc) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose?.();
        return;
      }
      if (!canStep) return;
      if (e.key === "ArrowRight") {
        onIndexChange?.((clampedIndex + 1) % total);
      }
      if (e.key === "ArrowLeft") {
        onIndexChange?.((clampedIndex - 1 + total) % total);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, currentSrc, canStep, clampedIndex, total, onClose, onIndexChange]);

  useEffect(() => {
    if (!isOpen || !currentSrc || typeof document === "undefined") return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, currentSrc]);

  if (!isOpen || !currentSrc) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[10060] flex items-center justify-center p-2 sm:p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black"
        onClick={onClose}
        aria-label="Close image preview"
      />

      {canStep ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange?.((clampedIndex - 1 + total) % total);
          }}
          className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-[10062] inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white/85 hover:bg-black/60 hover:text-white transition-colors"
          aria-label="Previous image"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
      ) : null}

      {canStep ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange?.((clampedIndex + 1) % total);
          }}
          className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-[10062] inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-black/45 text-white/85 hover:bg-black/60 hover:text-white transition-colors"
          aria-label="Next image"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      ) : null}

      <div
        className={cn(
          "relative z-[10061] w-full max-w-[min(96vw,1700px)] h-[92vh] sm:h-[94vh]",
          "flex items-center justify-center rounded-2xl border border-white/12",
          "bg-[#04070f] shadow-2xl overflow-hidden ring-1 ring-white/10"
        )}
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundImage:
            "radial-gradient(120% 120% at 50% 50%, rgba(22,22,22,0.75) 0%, rgba(0,0,0,0.92) 65%)",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-[10062] inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/14 bg-black/50 text-gray-200 hover:bg-black/70 transition-colors"
          aria-label="Close preview"
        >
          <X className="h-4 w-4" />
        </button>
        <img
          src={currentSrc}
          alt=""
          className="max-w-[96%] max-h-[90vh] sm:max-h-[92vh] w-auto object-contain select-none"
        />
      </div>
    </div>,
    document.body
  );
}
